<template>
    <VirtualAccountContainer>
        <!-- !왼쪽: 가상계좌 -->
        <FormSection>
            <Title>
                <Send :size="25" /> 가상계좌발송
            </Title>
            <NoticeSection>
                <div>* 가상계좌등록 5분 이내에 입금안내 문자가 자동발송됩니다.</div>
                <div>* 기존고객은 '내역불러오기'로 동일한 가상계좌를 사용할 수 있습니다.</div>
            </NoticeSection>
            <form @submit.prevent="handleSubmit">
                <div>
                    <FormGroup>
                        <Label>
                            고객명 <RedSpan>*</RedSpan>
                        </Label>
                        <InputContainer>
                            <Input type="text" v-model="cusnm" placeholder="신규고객명"
                                :class="{ error: errors.cusnm, 'blurred': isTestUser }" />
                            <CustomerHistoryButton @click.prevent.stop="getcusnm">
                                내역불러오기
                            </CustomerHistoryButton>
                        </InputContainer>
                    </FormGroup>
                    <MessageContainer v-if="errors.cusnm">
                        <ErrorMessage>{{ errors.cusnm }}</ErrorMessage>
                    </MessageContainer>
                </div>

                <div>
                    <FormGroup>
                        <Label>
                            연락처 <RedSpan>*</RedSpan>
                        </Label>
                        <InputContainer>
                            <Input type="text" placeholder="연락처 입력" v-model="mobile"
                                :class="{ error: errors.mobile, 'blurred': isTestUser }" @input="handleMobileInput"
                                @keydown.enter.prevent />
                        </InputContainer>
                    </FormGroup>
                    <MessageContainer>
                        <ErrorMessage v-if="errors.mobile">{{ errors.mobile }}</ErrorMessage>
                        <MobileCheckMessage v-if="mobileCheckMessage.show" v-html="mobileCheckMessage.text" />
                    </MessageContainer>
                </div>

                <FormGroup>
                    <Label>가상계좌번호</Label>
                    <Input type="text" v-model="vano" placeholder="가상계좌번호" readonly
                        style="background-color: #f5f5f5; text-align: center;" :class="{ 'blurred': isTestUser }" />
                </FormGroup>

                <FormGroup>
                    <Label>가상계좌은행</Label>
                    <Input type="text" v-model="bank" readonly style="background-color: #f5f5f5; text-align: center;"
                        :class="{ 'blurred': isTestUser }" />
                </FormGroup>

                <FormGroup>
                    <Label>
                        입금가능기간 <RedSpan>*</RedSpan>
                    </Label>
                    <DatePicker v-model:value="edate" placeholder="입금가능기간" style="width: 90%;"
                        :class="{ 'blurred': isTestUser }" :lang="ko" />
                </FormGroup>

                <div>
                    <FormGroup>
                        <Label>
                            항목명 <RedSpan>*</RedSpan>
                        </Label>
                        <Select v-model="payitemnm" :class="{ error: errors.payitemnm, 'blurred': isTestUser }">
                            <option value="">선택</option>
                            <option value="본인부담금">본인부담금</option>
                            <option value="비급여">비급여</option>
                            <option value="기타">기타</option>
                        </Select>
                    </FormGroup>
                    <MessageContainer v-if="errors.payitemnm">
                        <ErrorMessage>{{ errors.payitemnm }}</ErrorMessage>
                    </MessageContainer>
                </div>

                <div>
                    <FormGroup>
                        <Label>본인부담품목</Label>
                        <ItemsContainer>
                            <div v-for="(product, index) in products" :key="index">
                                <ModernProductItem>
                                    <CheckboxWrapper>
                                        <input type="checkbox" :id="'product-' + index" :value="index"
                                            v-model="selectedProductIds" @change="updatePaymentAmount" />
                                    </CheckboxWrapper>
                                    <ProductInfo>
                                        <ItemName>{{ product.name }}</ItemName>
                                    </ProductInfo>
                                    <ProductDetails>
                                        <DetailBadge>{{ product.quantity }}개</DetailBadge>
                                        <DetailBadge>{{ formatPrice(product.price) }}원</DetailBadge>
                                    </ProductDetails>
                                </ModernProductItem>
                            </div>
                        </ItemsContainer>
                    </FormGroup>
                </div>

                <!-- 입금금액 -->
                <FormGroup>
                    <Label>
                        입금금액 <RedSpan>*</RedSpan>
                    </Label>
                    <Input type="number" v-model="payamt" :class="{ error: errors.payamt, 'blurred': isTestUser }"
                        @keydown.enter.prevent />
                </FormGroup>
                <MessageContainer v-if="errors.payamt">
                    <ErrorMessage>{{ errors.payamt }}</ErrorMessage>
                </MessageContainer>

                <FormGroup>
                    <Label>비고</Label>
                    <TextArea v-model="remark" placeholder="비고를 입력하세요" @keydown.enter.prevent
                        :class="{ 'blurred': isTestUser }" />
                </FormGroup>

                <ButtonGroup>
                    <SubmitButton type="submit">발송</SubmitButton>
                    <CancelButton type="button" @click="closeModal">취소</CancelButton>
                </ButtonGroup>
            </form>
        </FormSection>

        <!-- !오른쪽: 가상계좌 -->
        <HistorySection>
            <Title>
                <PlaylistCheck :size="30" /> 가상계좌 발송내역
            </Title>

            <!-- 로딩 스피너 -->
            <LoadingOverlay v-if="isLoadingHistory">
                <LoadingSpinner></LoadingSpinner>
                <LoadingText>발송내역을 불러오는 중입니다...</LoadingText>
            </LoadingOverlay>

            <HistoryTable v-if="!isLoadingHistory">
                <thead>
                    <tr>
                        <Th>등록일</Th>
                        <Th>이름</Th>
                        <Th>전화번호</Th>
                        <Th>가상계좌번호</Th>
                        <Th>금액</Th>
                        <Th>항목명</Th>
                        <Th>입금여부</Th>
                        <Th style="width: 120px;">비고</Th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="pagedData.length === 0 && !isLoadingHistory">
                        <Td colspan="8" style="text-align: center; padding: 40px; color: #999;">
                            발송내역이 없습니다.
                        </Td>
                    </tr>
                    <tr v-for="(history, index) in pagedData" :key="index" :class="{ 'blurred': isTestUser }">
                        <Td>{{ formatDate(history.PAYMASDT) }}</Td>
                        <Td>{{ history.CUSNM }}</Td>
                        <Td>{{ history.MOBILENO }}</Td>
                        <Td>{{ history.VANO }}</Td>
                        <Td>{{ formatPrice(history.PAYITEMAMT) }}</Td>
                        <Td>{{ history.PAYITEMNM }}</Td>
                        <Td>
                            <StatusBadge :status="history.TRANST">
                                {{ getStatusText(history.TRANST) }}
                            </StatusBadge>
                        </Td>
                        <Td>{{ history.txt }}</Td>
                    </tr>
                </tbody>
            </HistoryTable>

            <!-- !페이지네이션 -->
            <PaginationContainer v-if="filteredTotalItems > 10">
                <PaginationButton :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
                    <ChevronLeft :size="17" class="chevron-icon" />
                </PaginationButton>
                <PaginationNumbers>
                    <PaginationNumber v-for="page in totalPages" :key="page" :class="{ active: currentPage === page }"
                        @click="changePage(page)">
                        {{ page }}
                    </PaginationNumber>
                </PaginationNumbers>
                <PaginationButton :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
                    <ChevronRight :size="17" class="chevron-icon" />
                </PaginationButton>
            </PaginationContainer>
        </HistorySection>
    </VirtualAccountContainer>
</template>

<script>
import * as VirtualAccountCss from '@/assets/styles/module/VirtualAccountCss.js';

import ChevronLeft from 'vue-material-design-icons/ChevronLeft.vue';
import ChevronRight from 'vue-material-design-icons/ChevronRight.vue';
import DatePicker from 'vue-datepicker-next';
import PlaylistCheck from 'vue-material-design-icons/PlaylistCheck.vue';
import Send from 'vue-material-design-icons/Send.vue';

export default {
    name: 'VirtualAccountModal',

    props: {
        products: {
            type: Array,
            required: true,
            default: () => []
        },
        orderData: {
            type: Object,
            required: true,
            default: () => ({
                name: '',
                number: '',
                phone1: ''
            })
        },
        orderId: {
            type: [String, Number],
            default: null
        },
        rentalTypes: {
            type: Object,
            default: () => ({
                hasOxygenGenerator: false,
                hasRentalProduct: false,
                hasDiabetesProduct: false,
                hasAssistanceProduct: false
            })
        },
    },

    components: {
        ...VirtualAccountCss,
        ChevronLeft,
        ChevronRight,
        DatePicker,
        PlaylistCheck,
        Send
    },

    setup() {
        const ko = {
            formatLocale: {
                months: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
                monthsShort: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
                weekdays: ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'],
                weekdaysShort: ['일', '월', '화', '수', '목', '금', '토'],
                weekdaysMin: ['일', '월', '화', '수', '목', '금', '토'],
                firstDayOfWeek: 0,
                firstWeekContainsDate: 1,
                meridiem: (h) => (h < 12 ? '오전' : '오후'),
                meridiemParse: /오전|오후/,
                isPM: (input) => input === '오후'
            },
            yearFormat: 'YYYY년',
            monthFormat: 'MMM',
            monthBeforeYear: false
        };

        return {
            ko
        };
    },

    data() {
        return {
            // 오른쪽 내역 관련
            accountHistory: [],
            currentPage: 1,
            totalItems: 0,
            itemsPerPage: 10,
            allAccountData: [], // 전체 가상계좌 데이터 저장용
            isLoadingHistory: false,

            // 왼쪽 폼 필드 (AccountSendModal 기능)
            cusnm: '',
            content1: '',
            mobile: '',
            vano: '신규생성',
            bank: '농협',
            edate: new Date(2099, 11, 31),
            payitemnm: '',
            payamt: 0,
            payreceiptno: '',
            remark: '',
            errors: {
                cusnm: '',
                mobile: '',
                payitemnm: '',
                payamt: ''
            },
            mobileCheckMessage: {
                show: false,
                text: ''
            },
            originalStyles: null,
            selectedProductIds: [],
            savedMobileForSearch: null // 계좌발급내역이 있는 연락처 저장
        };
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },
        // 부모로부터 전달받은 orderData.name에 해당하는 내역만 필터링합니다.
        filteredAccountHistory() {
            return this.accountHistory;
        },
        // 필터링된 데이터의 총 개수
        filteredTotalItems() {
            return this.filteredAccountHistory.length;
        },

        // 페이지네이션이 적용된 데이터
        pagedData() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            return this.filteredAccountHistory.slice(start, start + this.itemsPerPage);
        },
        // 필터링된 데이터 기준으로 총 페이지 수 계산
        totalPages() {
            return Math.ceil(this.filteredTotalItems / this.itemsPerPage);
        },
        displayedPages() {
            const range = 2;
            let start = Math.max(1, this.currentPage - range);
            let end = Math.min(this.totalPages, this.currentPage + range);
            const pages = [];
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            return pages;
        },
        displayPayamt: {
            get() {
                return this.payamt ? this.formatPrice(this.payamt) : '';
            },
            set(value) {
                const numericValue = value.replace(/[^0-9]/g, '');
                this.payamt = numericValue ? parseInt(numericValue) : 0;
            }
        },
        totalPrice() {
            return this.products
                .filter(product => this.selectedProductIds.includes(product.id))
                .reduce((sum, product) => sum + product.price * product.quantity, 0);
        }
    },

    methods: {
        closeModal() {
            // 스크롤 복원
            this.enableScroll();
            // 부모 컴포넌트의 상태 업데이트
            this.$emit('update:showVirtualAccountModal', false);
            // 모달 닫기 이벤트 발생
            this.$emit('close');
        },

        updatePaymentAmount() {
            let totalAmount = this.products
                .filter((_, index) => this.selectedProductIds.includes(index))
                .reduce((sum, product) => {
                    return sum + (product.price * product.quantity);
                }, 0);

            // 주문서 타입이 산소발생기인지 확인
            const isOxygenOrder = this.rentalTypes.hasOxygenGenerator ||
                this.orderData?.rental_type === 'o' ||
                (this.orderId && typeof this.orderId === 'object' && this.orderId?.rental_type === 'o');

            // 선택된 품목 중 산소발생기가 있는지 확인
            const hasOxygenProduct = this.products
                .filter((_, index) => this.selectedProductIds.includes(index))
                .some(product => {
                    const name1 = (product.name1 || '').toLowerCase();
                    const name2 = (product.name2 || '').toLowerCase();
                    const name = (product.name || '').toLowerCase();
                    return name1.includes('산소발생기') ||
                        name2.includes('산소발생기') ||
                        name.includes('산소발생기');
                });

            // 주문서 타입이 산소발생기이고, 품목이 산소발생기인 경우 금액을 1/10로 변환
            if (isOxygenOrder && hasOxygenProduct) {
                totalAmount = Math.floor(totalAmount / 10);
            }

            this.payamt = totalAmount;

            if (this.payamt < 500) {
                this.errors.payamt = '입금금액은 최소 500원 이상이어야 합니다.';
            } else {
                this.errors.payamt = '';
            }
        },

        // 오른쪽 내역 관련
        fetchAccountHistory() {
            this.isLoadingHistory = true;

            this.$axios
                .post(`${this.$apiUrls.hanqmedi}/virtualaccount/getlist`, {
                    search: "",
                    sdate: "1999-01-01",
                    edate: "2099-01-01",
                    status: "",
                    page: 1,
                    rows: 9999,
                    company: this.$globalState.company
                })
                .then(response => {
                    if (response.data.code === 200) {
                        // 현재 고객의 데이터만 필터링 (orderData.name 사용)
                        let accountData = response.data.data.filter(
                            item => item.CUSNM === this.orderData?.name
                        );

                        // 필터링된 데이터를 원하는 형태로 변환
                        accountData = accountData.map(item => ({
                            PAYMASDT: item.PAYMASDT,
                            VANO: item.VANO,
                            PAYITEMAMT: item.PAYITEMAMT,
                            PAYITEMNM: item.PAYITEMNM,
                            TRANST: this.getTransactionStatus(item),
                            txt: item.txt,
                            CUSNM: item.CUSNM,
                            MOBILENO: item.MOBILENO
                        }));

                        // 데이터가 10개 이상일 경우 중복 제거
                        if (accountData.length >= 10) {
                            const uniqueKeys = new Map();
                            accountData = accountData.filter(item => {
                                // 고객명, 금액, 항목명을 조합한 고유키 생성
                                const key = `${item.CUSNM}-${item.MOBILENO}-${item.PAYITEMAMT}-${item.PAYITEMNM}`;
                                if (!uniqueKeys.has(key)) {
                                    uniqueKeys.set(key, true);
                                    return true;
                                }
                                return false;
                            });
                        }

                        // 전체 데이터 저장 (이미 필터링된 데이터)
                        this.accountHistory = accountData;
                        this.allAccountData = accountData;
                        this.totalItems = accountData.length;
                    }
                })
                .catch(error => {
                    console.error('가상계좌 내역 조회 중 오류:', error);
                })
                .finally(() => {
                    this.isLoadingHistory = false;
                });
        },

        changePage(page) {
            this.currentPage = page;
        },

        getTransactionStatus(item) {
            if (item.RESULTCD === null) {
                return 'pending';
            } else if (item.RESULTCD !== 'D000') {
                return 'error';
            } else {
                return item.REGDT ? 'completed' : 'non-pay';
            }
        },

        getStatusText(status) {
            switch (status) {
                case 'pending':
                    return '등록대기';
                case 'error':
                    return '등록실패';
                case 'completed':
                    return '납부';
                case 'non-pay':
                    return '미납';
                default:
                    return '등록대기';
            }
        },

        // 왼쪽 폼
        getcusnm() {
            const styleElement = document.createElement('style');
            styleElement.textContent = `
                .swal2-container .customer-table tr {
                cursor: pointer;
                }
                .swal2-container .customer-table tr:hover {
                background-color: #ffeccc !important;
                }
            `;
            document.head.appendChild(styleElement);

            // 저장된 검색어 가져오기 (고객명 또는 연락처)
            const searchTerm = this.savedMobileForSearch || '';

            this.$swal.fire({
                title: '가상계좌 고객 불러오기',
                width: '1000px',
                html: `
                <div style="margin-bottom: 20px;">
                    <input type="text" id="searchCustomer" placeholder="고객명 또는 연락처 검색" style="width:98%; padding:8px; margin-bottom:10px;" />
                    <div id="customerList" style="max-height:500px; overflow-y:auto;"></div>
                </div>
                `,
                showConfirmButton: false,
                showCloseButton: true,
                didOpen: () => {
                    const searchInput = document.getElementById('searchCustomer');
                    if (searchInput) {
                        // 저장된 검색어를 입력 필드에 설정
                        searchInput.value = searchTerm;
                        searchInput.focus();

                        // Enter 키 이벤트 리스너
                        searchInput.addEventListener('keyup', (e) => {
                            if (e.key === 'Enter') {
                                this.search_customer(searchInput.value);
                            }
                        });

                        // 저장된 검색어가 있으면 자동으로 검색 실행
                        if (searchTerm) {
                            // 약간의 지연을 두어 DOM이 완전히 렌더링된 후 검색 실행
                            setTimeout(() => {
                                this.search_customer(searchTerm);
                            }, 100);
                        } else {
                            // 검색어가 없으면 빈 검색 실행
                            this.search_customer('');
                        }
                    }
                },
                willClose: () => {
                    if (styleElement && styleElement.parentNode) {
                        document.head.removeChild(styleElement);
                    }
                    // 모달 닫을 때 저장된 검색어 초기화
                    this.savedMobileForSearch = null;
                }
            });
        },

        async search_customer(search) {
            try {
                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/virtualaccount/search_customer`,
                    { search: search, company: this.$globalState.company }
                );
                if (response.data.code === 200) {
                    this.updateCustomerList(response.data.data);
                }
            } catch (error) {
                console.error('고객 검색 에러:', error);
                const customerList = document.getElementById('customerList');
                if (customerList) {
                    customerList.innerHTML = '<p style="color: red;">검색 중 오류가 발생했습니다.</p>';
                }
            }
        },

        updateCustomerList(results) {
            const customerList = document.getElementById('customerList');
            if (!customerList) return;

            let htmlContent = '';
            if (!results || results.length === 0) {
                htmlContent = `<div style="text-align:center; padding:8px;">검색결과가 없습니다.</div>`;
            } else {
                htmlContent = `
                <table class="customer-table" style="
                    width: 100%; 
                    border-collapse: collapse; 
                    table-layout: fixed;
                    max-width: 100%;
                ">
                <colgroup>
                    <col style="width: 15%;">  <!-- 최초등록일 -->
                    <col style="width: 15%;">  <!-- 고객명 -->
                    <col style="width: 20%;">  <!-- 연락처 -->
                    <col style="width: 25%;">  <!-- 가상계좌번호 -->
                    <col style="width: 25%;">  <!-- 비고 -->
                </colgroup>
                <thead>
                    <tr style="font-size: 15px;">
                        <th style="
                            padding: 12px 8px; 
                            border: 1px solid #ddd; 
                            background-color: #fb8b24; 
                            color: black;
                            text-align: center;
                            font-weight: bold;
                            position: sticky;
                            top: 0;
                            z-index: 10;
                        ">최초등록일</th>
                        <th style="
                            padding: 12px 8px; 
                            border: 1px solid #ddd; 
                            background-color: #fb8b24; 
                            color: black;
                            text-align: center;
                            font-weight: bold;
                            position: sticky;
                            top: 0;
                            z-index: 10;
                        ">고객명</th>
                        <th style="
                            padding: 12px 8px; 
                            border: 1px solid #ddd; 
                            background-color: #fb8b24; 
                            color: black;
                            text-align: center;
                            font-weight: bold;
                            position: sticky;
                            top: 0;
                            z-index: 10;
                        ">연락처</th>
                        <th style="
                            padding: 12px 8px; 
                            border: 1px solid #ddd; 
                            background-color: #fb8b24; 
                            color: black;
                            text-align: center;
                            font-weight: bold;
                            position: sticky;
                            top: 0;
                            z-index: 10;
                        ">가상계좌번호</th>
                        <th style="
                            padding: 12px 8px; 
                            border: 1px solid #ddd; 
                            background-color: #fb8b24; 
                            color: black;
                            text-align: center;
                            font-weight: bold;
                            position: sticky;
                            top: 0;
                            z-index: 10;
                        ">비고</th>
                    </tr>
                </thead>
                <tbody>
                    ${results.map(item => `
                        <tr style="cursor: pointer; transition: background-color 0.2s ease;" 
                        onclick='document.dispatchEvent(new CustomEvent("select-customer", { detail: ${JSON.stringify(item)} }))'
                        onmouseover="this.style.backgroundColor='#f8f9fa'"
                        onmouseout="this.style.backgroundColor='transparent'"
                        >
                            <td style="
                                padding: 12px 8px; 
                                border: 1px solid #ddd;
                                text-align: center;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                white-space: nowrap;
                                font-size: 14px;
                                ${this.isTestUser ? 'filter: blur(4px);' : ''}
                            ">${item.PAYMASDT || ''}</td>
                            <td style="
                                padding: 12px 8px; 
                                border: 1px solid #ddd;
                                text-align: center;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                white-space: nowrap;
                                font-weight: 500;
                                font-size: 14px;
                                ${this.isTestUser ? 'filter: blur(4px);' : ''}
                            ">${item.CUSNM || ''}</td>
                            <td style="
                                padding: 12px 8px; 
                                border: 1px solid #ddd;
                                text-align: center;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                white-space: nowrap;
                                font-family: monospace;
                                font-size: 14px;
                                ${this.isTestUser ? 'filter: blur(4px);' : ''}
                            ">${item.MOBILENO || ''}</td>
                            <td style="
                                padding: 12px 8px; 
                                border: 1px solid #ddd;
                                text-align: center;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                white-space: nowrap;
                                font-family: monospace;
                                font-size: 14px;
                                color: #007bff;
                                font-weight: 500;
                                ${this.isTestUser ? 'filter: blur(4px);' : ''}
                            ">${item.VANO || ''}</td>
                            <td style="
                                padding: 12px 8px; 
                                border: 1px solid #ddd;
                                text-align: center;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                white-space: nowrap;
                                font-size: 14px;
                                color: #666;
                                ${this.isTestUser ? 'filter: blur(4px);' : ''}
                            " title="${item.txt || ''}">${item.txt || ''}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
            `;
            }
            customerList.innerHTML = htmlContent;
        },

        select_customer(customer) {
            this.cusnm = customer.CUSNM;
            this.mobile = customer.MOBILENO;
            this.vano = customer.VANO;
            this.clearMobileCheckMessage();
            this.$swal.close();
        },

        handleMobileInput() {
            if (this.mobile.trim()) {
                this.mobile_check(this.mobile);
            } else {
                this.clearMobileCheckMessage();
            }
        },

        clearMobileCheckMessage() {
            this.mobileCheckMessage = { show: false, text: '' };
            this.savedMobileForSearch = null;
        },

        async mobile_check(data) {
            try {
                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/virtualaccount/mobile_check`,
                    { mobile: data, company: this.$globalState.company }
                );
                if (response.data.code === 200) {
                    if (response.data.data.length !== 0) {

                        let customerNameToSearch = null;

                        // 현재 입력된 고객명이 있으면 일치하는 고객 찾기
                        if (this.cusnm && this.cusnm.trim()) {
                            const matchedCustomer = response.data.data.find(
                                customer => customer.CUSNM && customer.CUSNM.trim() === this.cusnm.trim()
                            );
                            if (matchedCustomer) {
                                customerNameToSearch = matchedCustomer.CUSNM;
                            }
                        }

                        // 일치하는 고객이 없으면 첫 번째 고객의 이름 사용
                        if (!customerNameToSearch && response.data.data[0]) {
                            customerNameToSearch = response.data.data[0].CUSNM || data;
                        }

                        // 고객명이 없으면 연락처 사용
                        this.savedMobileForSearch = customerNameToSearch || data;

                        this.mobileCheckMessage = {
                            show: true,
                            text: `계좌발급내역이 있는 연락처입니다.<br/>
                                    '내역불러오기' 버튼을 눌러 기존계좌<br/>
                                    사용가능합니다.`
                        };
                    } else {
                        this.clearMobileCheckMessage();
                        this.savedMobileForSearch = null;
                    }
                } else if (response.data.code === 400) {
                    this.clearMobileCheckMessage();
                    this.savedMobileForSearch = null;
                } else {
                    this.mobileCheckMessage = {
                        show: true,
                        text: '서버오류가 발생했습니다.'
                    };
                    this.savedMobileForSearch = null;
                }
            } catch (error) {
                console.error('연락처 확인 중 오류:', error);
                this.mobileCheckMessage = {
                    show: true,
                    text: '서버오류가 발생했습니다.'
                };
                this.savedMobileForSearch = null;
            }
        },

        handleSubmit() {
            if (!this.validateForm()) {
                this.$swal.fire({
                    icon: 'warning',
                    title: '입력 확인',
                    text: '필수 항목을 모두 입력해주세요.',
                    confirmButtonColor: '#2563eb'
                });
                return;
            }

            const payload = {
                cusnm: this.cusnm, // 고객명
                content1: this.content1, // 인정번호
                mobile: this.mobile, // 연락처
                vano: this.vano, // 가상계좌번호
                edate: this.edate, // 입금가능기간
                payitemnm: this.payitemnm, // 항목명
                payamt: this.payamt, // 입금금액
                payreceiptno: this.payreceiptno, // 현금영수증번호
                remark: this.remark, // 비고
                thebill_id: this.$globalState.thebill_id, // 더빌 id
                company: this.$globalState.company
            };

            this.$axios
                .post(`${this.$apiUrls.hanqmedi}/virtualaccount/create`, payload)
                .then((response) => {
                    if (response.data.code === 200) {
                        this.$swal.fire({
                            icon: 'success',
                            title: '완료',
                            text: '가상계좌발송이 완료되었습니다.',
                            confirmButtonColor: '#2563eb'
                        }).then(() => {
                            // 가상계좌 발송 성공 시 이벤트 emit
                            if (this.orderId) {
                                this.$emit('virtual-account-sent', {
                                    orderId: this.orderId,
                                    paymentType: '가상계좌',
                                    paymentAmount: this.payamt,
                                    vano: this.vano,
                                    bank: this.bank
                                });
                            }

                            // 테이블 데이터 갱신
                            if (
                                window.virtual_account_vm &&
                                typeof window.virtual_account_vm.table_search === 'function'
                            ) {
                                window.virtual_account_vm.table_search();
                            }

                            // 발송 내역 새로고침 및 첫 페이지로 이동
                            this.currentPage = 1;
                            this.fetchAccountHistory();
                        });
                    } else {
                        this.$swal.fire({
                            icon: 'error',
                            title: '오류',
                            text: response.data.message || '가상계좌발송에 실패했습니다.<br> 1분 뒤에 다시 시도해주세요.',
                            confirmButtonColor: '#2563eb'
                        });
                    }
                })
                .catch((error) => {
                    console.error('가상계좌발송 오류:', error);
                    this.$swal.fire({
                        icon: 'error',
                        title: '오류',
                        text: '서버오류가 발생했습니다.',
                        confirmButtonColor: '#2563eb'
                    });
                });
        },

        validateForm() {
            this.errors = {
                cusnm: '',
                mobile: '',
                payitemnm: '',
                payamt: ''
            };
            let isValid = true;
            if (!this.cusnm.trim()) {
                this.errors.cusnm = '고객명을 입력해주세요.';
                isValid = false;
            }
            if (!this.mobile.trim()) {
                this.errors.mobile = '연락처를 입력해주세요.';
                isValid = false;
            }
            if (!this.payitemnm.trim()) {
                this.errors.payitemnm = '항목명을 선택해주세요.';
                isValid = false;
            }
            if (!this.payamt || Number(this.payamt) < 500) {
                this.errors.payamt = '입금금액은 최소 500원 이상이어야 합니다.';
                isValid = false;
            }
            return isValid;
        },

        disableScroll() {
            const noScrollStyle = {
                overflow: 'hidden',
                paddingRight: '15px'
            };
            this.originalStyles = {
                overflow: document.body.style.overflow,
                paddingRight: document.body.style.paddingRight
            };
            Object.assign(document.body.style, noScrollStyle);
        },

        enableScroll() {
            if (this.originalStyles) {
                document.body.style.overflow = this.originalStyles.overflow;
                document.body.style.paddingRight = this.originalStyles.paddingRight;
                this.originalStyles = null;
            }
        },

        formatPrice(price) {
            return new Intl.NumberFormat('ko-KR').format(price);
        },

        formatDate(dateString) {
            if (!dateString) return '';
            if (typeof dateString === 'string' && dateString.length === 8) {
                const year = dateString.substring(0, 4);
                const month = dateString.substring(4, 6);
                const day = dateString.substring(6, 8);
                return `${year}-${month}-${day}`;
            }
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }
            return dateString;
        },
    },

    mounted() {
        this.disableScroll();
        this._customerSelectHandler = (e) => {
            this.select_customer(e.detail);
        };
        document.addEventListener('select-customer', this._customerSelectHandler);
        this.fetchAccountHistory();
    },

    beforeUnmount() {
        this.enableScroll();
        document.removeEventListener('select-customer', this._customerSelectHandler);
        this.clearMobileCheckMessage();
        this.$emit('update:showVirtualAccountModal', false);
    },

    created() {
        this.selectedProductIds = this.products.map((_, index) => index); // 초기 선택된 품목들
        this.updatePaymentAmount(); // 초기 금액 계산

        if (this.orderData) {
            this.cusnm = this.orderData.name || '';
            this.mobile = this.orderData.phone1 || '';

            // 초기 연락처가 있을 경우 바로 검증 실행
            if (this.mobile.trim()) {
                this.mobile_check(this.mobile);
            }
        }
        this.fetchAccountHistory();
    },

    watch: {
        // accountHistory가 변경될 때마다 부모 컴포넌트에 건수 전달
        accountHistory: {
            handler(newValue) {
                // 현재 고객의 가상계좌 발송 건수만 필터링
                const count = newValue.filter(item => item.CUSNM === this.orderData.name).length;
                this.$emit('update:accountCount', count);
            },
            immediate: true
        }
    }
};
</script>

<style>
/* input 태그에만 블러 효과 적용 */
input.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}

/* 테이블 행에 블러 효과 적용 */
tr.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}

/* 테이블 셀에 블러 효과 적용 */
tr.blurred td,
tr.blurred .Td {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}

/* 다른 요소들에 blurred 클래스가 적용되어도 블러 처리하지 않음 */
.blurred:not(input):not(tr) {
    filter: none !important;
}

/* select, textarea 등 다른 요소들은 블러 처리하지 않음 */
select.blurred,
textarea.blurred,
.blurred .StatusBadge {
    filter: none !important;
}

/* input과 tr이 아닌 요소들은 명시적으로 블러 해제 */
input:not(.blurred),
tr:not(.blurred),
select:not(.blurred),
textarea:not(.blurred) {
    filter: none !important;
}
</style>